import { z } from "zod";
import { API_KEY_STATUSES } from "@artha/types";

// ─── API key management (creator-facing, JWT-authenticated) ──────────────────

export const CreateApiKeySchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(50),
  expiresInDays: z.coerce.number().int().min(1).max(365).optional(),
});
export type CreateApiKeyDto = z.infer<typeof CreateApiKeySchema>;

export const ApiKeyStatusSchema = z.enum(API_KEY_STATUSES);
export type ApiKeyStatusDto = z.infer<typeof ApiKeyStatusSchema>;

export const ApiKeySummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  keyPrefix: z.string(),
  last4: z.string(),
  status: ApiKeyStatusSchema,
  rateLimitPerMinute: z.number(),
  rateLimitPerDay: z.number(),
  lastUsedAt: z.string().nullable(),
  lastUsedIp: z.string().nullable(),
  expiresAt: z.string().nullable(),
  createdAt: z.string(),
});
export type ApiKeySummaryDto = z.infer<typeof ApiKeySummarySchema>;

export const CreateApiKeyResponseSchema = z.object({
  apiKey: ApiKeySummarySchema,
  /** Full secret key — returned exactly once at creation time. */
  key: z.string(),
});
export type CreateApiKeyResponseDto = z.infer<typeof CreateApiKeyResponseSchema>;

export const ApiKeyListResponseSchema = z.object({
  apiKeys: z.array(ApiKeySummarySchema),
});
export type ApiKeyListResponseDto = z.infer<typeof ApiKeyListResponseSchema>;

export const ApiKeyUsageQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional().default(20),
  offset: z.coerce.number().int().min(0).optional().default(0),
});
export type ApiKeyUsageQueryDto = z.infer<typeof ApiKeyUsageQuerySchema>;

export const ApiKeyUsageItemSchema = z.object({
  endpoint: z.string(),
  method: z.string(),
  statusCode: z.number(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  responseTimeMs: z.number().nullable(),
  createdAt: z.string(),
});
export type ApiKeyUsageItemDto = z.infer<typeof ApiKeyUsageItemSchema>;

export const ApiKeyUsageResponseSchema = z.object({
  items: z.array(ApiKeyUsageItemSchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
});
export type ApiKeyUsageResponseDto = z.infer<typeof ApiKeyUsageResponseSchema>;

// ─── Public API v1 (API-key-authenticated) response shapes ───────────────────
// Curated whitelist of job fields safe for external consumers. Internal and
// monetization fields (cpa/cpc values, ref/aggregator/feed ids, categories,
// raw apply_url) are intentionally excluded; `url` points to the creator's
// artha.link job page with click-attribution parameters pre-appended so
// traffic remains attributed to the creator.

export const PublicJobSchema = z.object({
  id: z.string(),
  slug: z.string().nullable(),
  title: z.string(),
  company: z.string(),
  logo: z.string().nullable(),
  description: z.string().nullable(),
  location: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  country: z.string().nullable(),
  job_type: z.string().nullable(),
  work_mode: z.string().nullable().optional(),
  salary_min: z.number().nullable(),
  salary_max: z.number().nullable(),
  salary_curr: z.string().nullable(),
  exp_min: z.number().nullable(),
  exp_max: z.number().nullable(),
  exp_unit: z.string().nullable(),
  skills: z.array(z.string()),
  posted_date: z.string(),
  url: z.string(),
  rpc: z.number().nullable().optional(),
  rpa: z.number().nullable().optional(),
});
export type PublicJobDto = z.infer<typeof PublicJobSchema>;

export const PublicJobsResponseSchema = z.object({
  items: z.array(PublicJobSchema),
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
  has_more: z.boolean(),
});
export type PublicJobsResponseDto = z.infer<typeof PublicJobsResponseSchema>;
